// Enhanced Real-time Data Updates Service
import type { QueryClient } from "@tanstack/react-query";

import { showToast } from "../utils/show-toast";
import { transformErrorResponse } from "../utils/transform-error-response";

// Real-time event types
export interface RealTimeEvent {
  type: 'data_updated' | 'subscription_changed' | 'user_updated' | 'appointment_updated' | 
        'lead_updated' | 'communication_updated' | 'reminder_updated' | 'resource_updated' |
        'bulk_update' | 'system_update';
  entity: 'appointments' | 'leads' | 'communications' | 'reminders' | 'resources' | 
          'subscriptions' | 'users' | 'notifications' | 'system';
  action: 'created' | 'updated' | 'deleted' | 'status_changed' | 'bulk_updated';
  data: unknown;
  metadata?: {
    userId?: string;
    entityId?: string;
    entityIds?: string[];
    timestamp: string;
    source?: string;
    changes?: Record<string, unknown>;
  };
}

// Subscription status change event
export interface SubscriptionStatusEvent {
  type: 'subscription_changed';
  userId: string;
  subscriptionId: string;
  oldStatus: string;
  newStatus: string;
  planId?: string;
  planName?: string;
  effectiveDate: string;
  metadata: {
    timestamp: string;
    source: 'stripe' | 'admin' | 'system';
    reason?: string;
  };
}

// Cache invalidation strategy
export interface CacheInvalidationStrategy {
  entity: string;
  action: string;
  queryKeys: string[][];
  invalidationType: 'exact' | 'prefix' | 'pattern';
  shouldRefetch?: boolean;
  delay?: number; // Delay before invalidation in ms
}

// Real-time connection status
export interface ConnectionStatus {
  isConnected: boolean;
  lastConnected?: Date;
  lastDisconnected?: Date;
  reconnectAttempts: number;
  connectionId?: string;
}

// Enhanced Real-time Service Class
class EnhancedRealTimeService {
  private eventSource: EventSource | null = null;
  private queryClient: QueryClient | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectDelay = 1000; // Start with 1 second
  private maxReconnectDelay = 30000; // Max 30 seconds
  private listeners = new Map<string, Set<(event: RealTimeEvent) => void>>();
  private connectionStatus: ConnectionStatus = {
    isConnected: false,
    reconnectAttempts: 0,
  };

  // Cache invalidation strategies for different entities
  private cacheStrategies: CacheInvalidationStrategy[] = [
    // Appointments
    {
      entity: 'appointments',
      action: 'created',
      queryKeys: [['appointments'], ['appointments', 'stats']],
      invalidationType: 'prefix',
      shouldRefetch: true,
    },
    {
      entity: 'appointments',
      action: 'updated',
      queryKeys: [['appointments'], ['appointments', 'stats']],
      invalidationType: 'prefix',
      shouldRefetch: true,
    },
    {
      entity: 'appointments',
      action: 'deleted',
      queryKeys: [['appointments'], ['appointments', 'stats']],
      invalidationType: 'prefix',
      shouldRefetch: true,
    },

    // Leads
    {
      entity: 'leads',
      action: 'created',
      queryKeys: [['leads'], ['leads', 'stats']],
      invalidationType: 'prefix',
      shouldRefetch: true,
    },
    {
      entity: 'leads',
      action: 'updated',
      queryKeys: [['leads'], ['leads', 'stats']],
      invalidationType: 'prefix',
      shouldRefetch: true,
    },
    {
      entity: 'leads',
      action: 'deleted',
      queryKeys: [['leads'], ['leads', 'stats']],
      invalidationType: 'prefix',
      shouldRefetch: true,
    },

    // Communications
    {
      entity: 'communications',
      action: 'created',
      queryKeys: [['communications'], ['communications', 'stats']],
      invalidationType: 'prefix',
      shouldRefetch: true,
    },
    {
      entity: 'communications',
      action: 'updated',
      queryKeys: [['communications'], ['communications', 'stats']],
      invalidationType: 'prefix',
      shouldRefetch: true,
    },

    // Reminders
    {
      entity: 'reminders',
      action: 'created',
      queryKeys: [['reminders'], ['reminders', 'stats']],
      invalidationType: 'prefix',
      shouldRefetch: true,
    },
    {
      entity: 'reminders',
      action: 'updated',
      queryKeys: [['reminders'], ['reminders', 'stats']],
      invalidationType: 'prefix',
      shouldRefetch: true,
    },
    {
      entity: 'reminders',
      action: 'deleted',
      queryKeys: [['reminders'], ['reminders', 'stats']],
      invalidationType: 'prefix',
      shouldRefetch: true,
    },

    // Resources
    {
      entity: 'resources',
      action: 'created',
      queryKeys: [['resources'], ['resources', 'stats'], ['storage']],
      invalidationType: 'prefix',
      shouldRefetch: true,
    },
    {
      entity: 'resources',
      action: 'updated',
      queryKeys: [['resources'], ['resources', 'stats'], ['storage']],
      invalidationType: 'prefix',
      shouldRefetch: true,
    },
    {
      entity: 'resources',
      action: 'deleted',
      queryKeys: [['resources'], ['resources', 'stats'], ['storage']],
      invalidationType: 'prefix',
      shouldRefetch: true,
    },

    // Subscriptions
    {
      entity: 'subscriptions',
      action: 'status_changed',
      queryKeys: [['subscriptions'], ['transactions'], ['user', 'profile']],
      invalidationType: 'prefix',
      shouldRefetch: true,
      delay: 2000, // Delay to allow backend processing
    },

    // Users
    {
      entity: 'users',
      action: 'updated',
      queryKeys: [['user'], ['users']],
      invalidationType: 'prefix',
      shouldRefetch: true,
    },

    // Notifications
    {
      entity: 'notifications',
      action: 'created',
      queryKeys: [['notifications']],
      invalidationType: 'prefix',
      shouldRefetch: true,
    },
    {
      entity: 'notifications',
      action: 'updated',
      queryKeys: [['notifications']],
      invalidationType: 'prefix',
      shouldRefetch: true,
    },
    {
      entity: 'notifications',
      action: 'deleted',
      queryKeys: [['notifications']],
      invalidationType: 'prefix',
      shouldRefetch: true,
    },
  ];

  // Initialize the service with QueryClient
  initialize(queryClient: QueryClient): void {
    this.queryClient = queryClient;
  }

  // Connect to real-time updates
  connect(endpoint = '/api/realtime/stream'): void {
    try {
      // Close existing connection if any
      this.disconnect();

      // Create new EventSource connection
      this.eventSource = new EventSource(endpoint, {
        withCredentials: true,
      });

      this.eventSource.onopen = () => {
        this.connectionStatus = {
          isConnected: true,
          lastConnected: new Date(),
          reconnectAttempts: 0,
          connectionId: this.generateConnectionId(),
        };
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
        
        // Notify listeners about connection
        this.emitConnectionEvent('connected');
      };

      this.eventSource.onmessage = (event) => {
        try {
          const realTimeEvent: RealTimeEvent = JSON.parse(event.data);
          this.handleRealTimeEvent(realTimeEvent);
        } catch (error) {

        }
      };

      this.eventSource.onerror = (error) => {

        this.connectionStatus.isConnected = false;
        this.connectionStatus.lastDisconnected = new Date();
        
        // Notify listeners about disconnection
        this.emitConnectionEvent('disconnected');
        
        this.handleReconnection();
      };

    } catch (error) {

      this.connectionStatus.isConnected = false;
    }
  }

  // Disconnect from real-time updates
  disconnect(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
      this.connectionStatus.isConnected = false;
      this.connectionStatus.lastDisconnected = new Date();
      
      // Notify listeners about disconnection
      this.emitConnectionEvent('disconnected');
    }
  }

  // Handle reconnection with exponential backoff
  private handleReconnection(): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      this.connectionStatus.reconnectAttempts = this.reconnectAttempts;

      setTimeout(() => {
        this.connect();
      }, this.reconnectDelay);

      // Exponential backoff with jitter
      this.reconnectDelay = Math.min(
        this.reconnectDelay * 2 + Math.random() * 1000,
        this.maxReconnectDelay
      );
    } else {

      showToast("Real-time updates disconnected. Please refresh the page.", "warning");
      this.connectionStatus.isConnected = false;
    }
  }

  // Handle real-time events
  private handleRealTimeEvent(event: RealTimeEvent): void {

    // Emit event to specific listeners
    const eventKey = `${event.entity}:${event.action}`;
    const eventListeners = this.listeners.get(eventKey);
    if (eventListeners) {
      eventListeners.forEach(listener => {
        try {
          listener(event);
        } catch (error) {

        }
      });
    }

    // Emit to general listeners
    const generalListeners = this.listeners.get('*');
    if (generalListeners) {
      generalListeners.forEach(listener => {
        try {
          listener(event);
        } catch (error) {

        }
      });
    }

    // Handle cache invalidation
    this.handleCacheInvalidation(event);

    // Handle specific event types
    this.handleSpecificEvents(event);
  }

  // Handle cache invalidation based on event
  private handleCacheInvalidation(event: RealTimeEvent): void {
    if (!this.queryClient) {

      return;
    }

    // Find matching cache strategies
    const strategies = this.cacheStrategies.filter(
      strategy => strategy.entity === event.entity && strategy.action === event.action
    );

    if (strategies.length === 0) {
      return;
    }

    strategies.forEach(strategy => {
      const invalidateCache = () => {
        strategy.queryKeys.forEach(queryKey => {
          try {
            switch (strategy.invalidationType) {
              case 'exact':
                this.queryClient!.invalidateQueries({
                  queryKey,
                  exact: true,
                  refetchType: strategy.shouldRefetch ? 'active' : 'none',
                });
                break;
              
              case 'prefix':
                this.queryClient!.invalidateQueries({
                  queryKey,
                  refetchType: strategy.shouldRefetch ? 'active' : 'none',
                });
                break;
              
              case 'pattern':
                // For pattern matching, we'll use predicate function
                this.queryClient!.invalidateQueries({
                  predicate: (query) => {
                    const key = query.queryKey;
                    return key.some(part => 
                      typeof part === 'string' && 
                      queryKey.some(pattern => 
                        typeof pattern === 'string' && part.includes(pattern)
                      )
                    );
                  },
                  refetchType: strategy.shouldRefetch ? 'active' : 'none',
                });
                break;
            }
            
          } catch (error) {

          }
        });
      };

      // Apply delay if specified
      if (strategy.delay) {
        setTimeout(invalidateCache, strategy.delay);
      } else {
        invalidateCache();
      }
    });
  }

  // Handle specific event types
  private handleSpecificEvents(event: RealTimeEvent): void {
    switch (event.type) {
      case 'subscription_changed':
        // Attempt to treat the incoming event as a SubscriptionStatusEvent
        // If the backend uses the unified RealTimeEvent shape, try to adapt from event.data
        if ((event as unknown as SubscriptionStatusEvent).newStatus) {
          this.handleSubscriptionStatusChange(event as unknown as SubscriptionStatusEvent);
        } else if (event && typeof event === 'object' && event.data && typeof event.data === 'object') {
          const d = event.data as Record<string, unknown>;
          const adapted: SubscriptionStatusEvent = {
            type: 'subscription_changed',
            userId: typeof d.userId === 'string' ? d.userId : '',
            subscriptionId: typeof d.subscriptionId === 'string' ? d.subscriptionId : '',
            oldStatus: typeof d.oldStatus === 'string' ? d.oldStatus : '',
            newStatus: typeof d.newStatus === 'string' ? d.newStatus : '',
            planId: typeof d.planId === 'string' ? d.planId : undefined,
            planName: typeof d.planName === 'string' ? d.planName : undefined,
            effectiveDate: typeof d.effectiveDate === 'string' ? d.effectiveDate : new Date().toISOString(),
            metadata: {
              timestamp: typeof d.timestamp === 'string' ? d.timestamp : new Date().toISOString(),
              source: (d.source === 'stripe' || d.source === 'admin' || d.source === 'system') ? (d.source as 'stripe' | 'admin' | 'system') : 'system',
              reason: typeof d.reason === 'string' ? d.reason : undefined,
            },
          };
          this.handleSubscriptionStatusChange(adapted);
        }
        break;
      
      case 'appointment_updated':
        this.handleAppointmentUpdate(event);
        break;
      
      case 'lead_updated':
        this.handleLeadUpdate(event);
        break;
      
      case 'communication_updated':
        this.handleCommunicationUpdate(event);
        break;
      
      case 'system_update':
        this.handleSystemUpdate(event);
        break;
    }
  }

  // Handle subscription status changes
  private handleSubscriptionStatusChange(event: SubscriptionStatusEvent): void {
    
    // Show appropriate toast message
    const statusMessages = {
      active: 'Your subscription is now active!',
      canceled: 'Your subscription has been canceled.',
      past_due: 'Your subscription payment is past due.',
      trialing: 'Your trial period has started.',
      expired: 'Your subscription has expired.',
    };

    const message = statusMessages[event.newStatus as keyof typeof statusMessages] || 
                   `Your subscription status has changed to ${event.newStatus}.`;
    
    const toastType = event.newStatus === 'active' ? 'success' : 
                     event.newStatus === 'canceled' || event.newStatus === 'expired' ? 'error' : 
                     'warning';
    
    showToast(message, toastType);

    // Invalidate subscription-related queries with delay
    setTimeout(() => {
      if (this.queryClient) {
        this.queryClient.invalidateQueries({
          queryKey: ['subscriptions'],
          refetchType: 'active',
        });
        this.queryClient.invalidateQueries({
          queryKey: ['user', 'profile'],
          refetchType: 'active',
        });
      }
    }, 2000);
  }

  // Handle appointment updates
  private handleAppointmentUpdate(event: RealTimeEvent): void {
    if (event.action === 'created') {
      showToast('New appointment scheduled', 'info');
    } else if (event.action === 'updated') {
      showToast('Appointment updated', 'info');
    } else if (event.action === 'deleted') {
      showToast('Appointment canceled', 'warning');
    }
  }

  // Handle lead updates
  private handleLeadUpdate(event: RealTimeEvent): void {
    const data = (event.data && typeof event.data === 'object') ? (event.data as Record<string, unknown>) : {};
    if (event.action === 'created') {
      showToast('New lead added', 'success');
    } else if (event.action === 'status_changed') {
      const leadName = typeof data.name === 'string' ? data.name : 'Lead';
      const newStatus = typeof data.status === 'string' ? data.status : 'updated';
      showToast(`${leadName} status changed to ${newStatus}`, 'info');
    }
  }

  // Handle communication updates
  private handleCommunicationUpdate(event: RealTimeEvent): void {
    const data = (event.data && typeof event.data === 'object') ? (event.data as Record<string, unknown>) : {};
    if (event.action === 'created') {
      const type = typeof data.type === 'string' ? data.type : 'message';
      showToast(`New ${type} received`, 'info');
    }
  }

  // Handle system updates
  private handleSystemUpdate(event: RealTimeEvent): void {
    const data = (event.data && typeof event.data === 'object') ? (event.data as Record<string, unknown>) : {};
    const message = typeof data.message === 'string' ? data.message : undefined;
    if (message) {
      const priority = typeof data.priority === 'string' ? data.priority : 'info';
      const toastType = priority === 'high' || priority === 'urgent' ? 'warning' : 'info';
      showToast(message, toastType);
    }
  }

  // Event listener management
  addEventListener(
    eventKey: string, // Format: 'entity:action' or '*' for all events
    listener: (event: RealTimeEvent) => void
  ): void {
    if (!this.listeners.has(eventKey)) {
      this.listeners.set(eventKey, new Set());
    }
    this.listeners.get(eventKey)!.add(listener);
  }

  removeEventListener(
    eventKey: string,
    listener: (event: RealTimeEvent) => void
  ): void {
    const eventListeners = this.listeners.get(eventKey);
    if (eventListeners) {
      eventListeners.delete(listener);
      if (eventListeners.size === 0) {
        this.listeners.delete(eventKey);
      }
    }
  }

  // Connection event handling
  private emitConnectionEvent(status: 'connected' | 'disconnected'): void {
    const connectionListeners = this.listeners.get('connection');
    if (connectionListeners) {
      const event: RealTimeEvent = {
        type: 'system_update',
        entity: 'system',
        action: status === 'connected' ? 'created' : 'deleted',
        data: { connectionStatus: status },
        metadata: {
          timestamp: new Date().toISOString(),
          source: 'realtime-service',
        },
      };

      connectionListeners.forEach(listener => {
        try {
          listener(event);
        } catch (error) {

        }
      });
    }
  }

  // Utility methods
  private generateConnectionId(): string {
    return `conn_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
  }

  // Get connection status
  getConnectionStatus(): ConnectionStatus {
    return { ...this.connectionStatus };
  }

  // Force cache invalidation for specific entity
  invalidateEntityCache(entity: string, action?: string): void {
    if (!this.queryClient) {

      return;
    }

    const strategies = this.cacheStrategies.filter(
      strategy => strategy.entity === entity && (!action || strategy.action === action)
    );

    strategies.forEach(strategy => {
      strategy.queryKeys.forEach(queryKey => {
        this.queryClient!.invalidateQueries({
          queryKey,
          refetchType: 'active',
        });
      });
    });

  }

  // Add custom cache invalidation strategy
  addCacheStrategy(strategy: CacheInvalidationStrategy): void {
    this.cacheStrategies.push(strategy);
  }

  // Remove cache invalidation strategy
  removeCacheStrategy(entity: string, action: string): void {
    this.cacheStrategies = this.cacheStrategies.filter(
      strategy => !(strategy.entity === entity && strategy.action === action)
    );
  }

  // Get active listeners count
  getListenersCount(): number {
    let total = 0;
    this.listeners.forEach(listeners => {
      total += listeners.size;
    });
    return total;
  }

  // Cleanup method
  cleanup(): void {
    this.disconnect();
    this.listeners.clear();
    this.queryClient = null;
  }

  // Health check
  isHealthy(): boolean {
    return this.connectionStatus.isConnected && 
           this.eventSource?.readyState === EventSource.OPEN;
  }

  // Get service statistics
  getStats(): {
    isConnected: boolean;
    reconnectAttempts: number;
    listenersCount: number;
    cacheStrategiesCount: number;
    lastConnected?: Date;
    lastDisconnected?: Date;
  } {
    return {
      isConnected: this.connectionStatus.isConnected,
      reconnectAttempts: this.connectionStatus.reconnectAttempts,
      listenersCount: this.getListenersCount(),
      cacheStrategiesCount: this.cacheStrategies.length,
      lastConnected: this.connectionStatus.lastConnected,
      lastDisconnected: this.connectionStatus.lastDisconnected,
    };
  }
}

// Export singleton instance
export const realTimeService = new EnhancedRealTimeService();
export default realTimeService;